import React from "react";
import { inr } from "./labels";

// Rough phone preview of a home feed, styled with the app's design tokens.
// It shows content and order; pixel details live in the app.

const IMG_BASE = "https://api.barbera.in";
const img = (path) => (!path ? null : path.startsWith("http") ? path : `${IMG_BASE}${path}`);

const ink = "#1B1726";
const muted = "#6E6880";
const brandStrong = "#5A36C4";
const success = "#1C7F52";

const Title = ({ s }) =>
  s.title ? (
    <div className="flex items-end justify-between px-3 pb-2">
      <div>
        <div style={{ color: ink }} className="text-[14px] font-bold leading-tight">{s.title}</div>
        {s.subtitle && <div style={{ color: muted }} className="text-[10.5px]">{s.subtitle}</div>}
      </div>
      {s.totalCount > (s.services?.length || 0) && (
        <span style={{ color: brandStrong }} className="text-[10.5px] font-bold whitespace-nowrap">See all {s.totalCount} ›</span>
      )}
    </div>
  ) : null;

const Card = ({ svc, cardStyle }) => {
  const width = cardStyle === "PACKAGE" ? 170 : cardStyle === "LARGE" ? 140 : 96;
  return (
    <div style={{ width }} className="flex-none flex flex-col gap-0.5">
      <div className="relative rounded-lg overflow-hidden bg-[#F3EEFE]" style={{ aspectRatio: cardStyle === "PACKAGE" ? "16/9" : cardStyle === "LARGE" ? "4/3" : "1/1" }}>
        {svc.image && <img src={img(svc.image)} alt="" className="w-full h-full object-cover" />}
        {svc.badge && <span className="absolute top-1 left-1 bg-white text-[8px] font-bold px-1 rounded" style={{ color: brandStrong }}>{svc.badge}</span>}
      </div>
      {cardStyle === "PACKAGE" && svc.variant && <span className="text-[8.5px] font-semibold text-[#A8620A] bg-[#FFF3E0] rounded px-1 self-start">{svc.variant}</span>}
      <div style={{ color: ink }} className="text-[10.5px] font-semibold leading-tight line-clamp-2">{svc.name}</div>
      <div style={{ color: muted }} className="text-[9px]">
        {svc.ratingCount ? <span style={{ color: success }} className="font-bold">★ {svc.ratingAvg} </span> : null}
        {svc.ratingCount ? `(${svc.ratingCount}) · ` : ""}
        {svc.durationMinutes ? `${svc.durationMinutes} min` : ""}
      </div>
      <div style={{ color: ink }} className="text-[11px] font-bold">
        {svc.campaignPrice ? inr(svc.campaignPrice) : inr(svc.price)}
        {svc.campaignPrice ? <s className="ml-1 font-normal" style={{ color: muted }}>{inr(svc.price)}</s> : null}
      </div>
      {svc.bookedLast30Days ? <div className="text-[8.5px] font-semibold text-[#C2264B]">{svc.bookedLast30Days}+ booked this month</div> : null}
    </div>
  );
};

const Rail = ({ children }) => <div className="flex gap-2 overflow-x-auto px-3 pb-1">{children}</div>;

const Section = ({ s }) => {
  switch (s.type) {
    case "LIVE_BOOKING":
      return (s.liveBookings || []).map((b, i) => (
        <div key={i} className="mx-3 rounded-lg px-3 py-2 text-white text-[10.5px]" style={{ background: ink }}>
          <b>{b.beauticianFirstName || "Your beautician"}</b> · {b.serviceSummary} · {b.status}
        </div>
      ));
    case "HERO_CAROUSEL":
      return (
        <div className="mx-3 rounded-xl overflow-hidden bg-[#F3EEFE]" style={{ aspectRatio: "16/9" }}>
          {s.banners?.[0] && <img src={img(s.banners[0].image)} alt="" className="w-full h-full object-cover" />}
          <div className="text-center text-[9px]" style={{ color: muted }}>{s.banners?.length} banner(s)</div>
        </div>
      );
    case "CATEGORY_GRID":
      return (
        <>
          <Title s={s} />
          <div className="grid grid-cols-4 gap-2 px-3">
            {s.categories.map((c) => (
              <div key={c.id} className="flex flex-col items-center gap-1">
                <img src={img(c.image)} alt="" className="w-10 h-10 rounded-xl object-cover bg-[#F3EEFE]" />
                <span className="text-[9px] text-center leading-tight" style={{ color: ink }}>{c.name}</span>
              </div>
            ))}
          </div>
        </>
      );
    case "COUPONS":
      return (
        <>
          <Title s={s} />
          <Rail>
            {s.coupons.map((c) => (
              <div key={c.code} className="flex-none w-40 rounded-lg border border-dashed px-2 py-1.5" style={{ borderColor: c.newCustomersOnly ? "#9ED9B9" : "#DCCFFB" }}>
                <div className="text-[10.5px] font-bold" style={{ color: ink }}>{c.title}</div>
                <div className="text-[9px]" style={{ color: muted }}>{c.description}</div>
                <div className="text-[9px] font-mono font-semibold" style={{ color: brandStrong }}>{c.code}</div>
              </div>
            ))}
          </Rail>
        </>
      );
    case "RATING_PROMPT":
      return (
        <div className="mx-3 rounded-lg border px-3 py-2 text-[10.5px]" style={{ color: ink }}>
          How was your {s.ratingPrompt.serviceSummary}
          {s.ratingPrompt.beauticianFirstName ? ` with ${s.ratingPrompt.beauticianFirstName}` : ""}? ★★★★★
          {s.ratingPrompt.rewardText && <div style={{ color: muted }} className="text-[9px]">{s.ratingPrompt.rewardText}</div>}
        </div>
      );
    case "DEAL": {
      const d = s.services[0];
      return (
        <>
          <Title s={s} />
          <div className="mx-3 rounded-xl px-3 py-2 text-white" style={{ background: "linear-gradient(120deg,#2A1640,#5A2A6E 60%,#A0397A)" }}>
            <div className="text-[9px] font-bold tracking-wider text-[#FFC98A]">TODAY ONLY</div>
            <div className="text-[12px] font-bold">{d.name}</div>
            <div className="text-[14px] font-extrabold">{inr(d.campaignPrice)} <s className="text-[10px] font-normal opacity-70">{inr(d.price)}</s></div>
          </div>
        </>
      );
    }
    case "MOST_BOOKED":
      return (
        <>
          <Title s={s} />
          {s.chips?.length > 0 && (
            <div className="flex gap-1 px-3 pb-2 overflow-x-auto">
              {["All", ...s.chips].map((c, i) => (
                <span key={c} className={`flex-none text-[9px] px-2 py-0.5 rounded-full border ${i === 0 ? "bg-[#1B1726] text-white" : ""}`}>{c}</span>
              ))}
            </div>
          )}
          <Rail>{s.services.slice(0, 6).map((svc) => <Card key={svc.id} svc={svc} cardStyle="COMPACT" />)}</Rail>
        </>
      );
    case "SERVICE_RAIL":
    case "BOOK_AGAIN":
      return (
        <>
          <Title s={s} />
          <Rail>{s.services.map((svc) => <Card key={svc.id} svc={svc} cardStyle={s.cardStyle || "COMPACT"} />)}</Rail>
        </>
      );
    case "TRUST": {
      const t = s.trust;
      return (
        <>
          <Title s={s} />
          <div className="mx-3 rounded-xl bg-[#E7F5EE] p-2 grid grid-cols-3 text-center text-[#16573A]">
            <div><b className="block text-[13px]">{t.completedServices.toLocaleString("en-IN")}</b><span className="text-[8.5px]">services completed</span></div>
            <div><b className="block text-[13px]">{t.ratingAvg ? `${t.ratingAvg}★` : "—"}</b><span className="text-[8.5px]">{t.ratingCount} ratings</span></div>
            <div><b className="block text-[13px]">{t.bookingsLast30Days}</b><span className="text-[8.5px]">in last 30 days</span></div>
            {t.promises?.length > 0 && <div className="col-span-3 text-left text-[8.5px] mt-1">{t.promises.map((p) => `✓ ${p}`).join("  ")}</div>}
          </div>
        </>
      );
    }
    case "REFER":
      return (
        <div className="mx-3 rounded-xl bg-[#F3EEFE] px-3 py-2 text-[11px] font-bold" style={{ color: ink }}>
          Give ₹{s.refer.referrerReward}, get ₹{s.refer.refereeReward}
        </div>
      );
    case "CONSULT":
      return (
        <div className="mx-3 rounded-xl bg-[#FFF0F6] px-3 py-2">
          <div className="text-[11px] font-bold" style={{ color: ink }}>{s.title}</div>
          <div className="text-[9.5px]" style={{ color: muted }}>{s.consult.body}</div>
          <span className="inline-block mt-1 text-[9.5px] font-bold text-white rounded px-2 py-1 bg-[#B2457A]">{s.consult.ctaLabel}</span>
        </div>
      );
    case "PACKAGE_BUILDER":
      return s.packageBuilder ? (
        <div className="mx-3 rounded-xl px-3 py-2.5 text-white" style={{ background: "linear-gradient(135deg,#5A36C4,#7F58E6)" }}>
          <div className="text-[12px] font-bold">{s.packageBuilder.title}</div>
          <div className="text-[9.5px] opacity-90">{s.packageBuilder.subtitle || `Pick ${s.packageBuilder.minItems}+ services, save up to ${s.packageBuilder.maxPercent}%`}</div>
          <span className="inline-block mt-1 text-[9.5px] font-bold rounded px-2 py-1 bg-white" style={{ color: "#5A36C4" }}>Start building</span>
        </div>
      ) : null;
    case "OCCASIONS":
      return (
        <>
          <Title s={s} />
          <div className="grid grid-cols-2 gap-2 px-3">
            {(s.occasions || []).map((o) => (
              <div key={o.categoryId} className="relative h-[86px] rounded-xl overflow-hidden bg-[#F3EEFE]">
                {o.image && <img src={o.image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                <div className="absolute inset-0" style={{ background: "linear-gradient(transparent 35%, rgba(0,0,0,.65))" }} />
                <div className="absolute left-2 bottom-1.5 text-white">
                  <div className="text-[10.5px] font-bold">{o.name}</div>
                  {o.fromPrice && <div className="text-[9px] opacity-90">from ₹{o.fromPrice.toLocaleString("en-IN")}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    case "JOURNEY":
      return (
        <>
          <Title s={s} />
          <div className="mx-3 flex flex-col gap-1.5">
            {(s.journey || []).map((st, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-[#F3EEFE] text-[8.5px] font-bold flex items-center justify-center" style={{ color: "#5A36C4" }}>{i + 1}</span>
                <div><div className="text-[10px] font-bold" style={{ color: ink }}>{st.title}</div>{st.body && <div className="text-[9px]" style={{ color: muted }}>{st.body}</div>}</div>
              </div>
            ))}
          </div>
        </>
      );
    case "FAQ":
      return (
        <>
          <Title s={s} />
          <div className="mx-3 rounded-xl border divide-y">
            {(s.faqs || []).map((f, i) => (
              <div key={i} className="px-2.5 py-1.5 text-[10px] flex justify-between" style={{ color: ink }}>{f.question}<span>⌄</span></div>
            ))}
          </div>
        </>
      );
    default:
      return null;
  }
};

const FeedPreview = ({ feed, loading, error }) => (
  <div className="mx-auto w-[300px] h-[620px] rounded-[32px] border-[8px] border-[#15121C] bg-white overflow-y-auto shadow-xl" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
    <div className="sticky top-0 z-10 px-3 pt-3 pb-2" style={{ background: "linear-gradient(#EFE8FF,#fff)" }}>
      <div className="text-[11px] font-bold" style={{ color: ink }}>Home ▾ <span className="font-normal" style={{ color: muted }}>Malviya Nagar, Jaipur</span></div>
      <div className="mt-2 rounded-lg border bg-white px-2 py-1.5 text-[10px] text-[#A9A3B8]">
        Search for {feed?.searchHints?.[0] ? `"${feed.searchHints[0]}"` : "services"}
      </div>
    </div>
    {loading && <div className="p-6 text-center text-xs text-gray-500">Loading preview…</div>}
    {error && <div className="p-6 text-center text-xs text-red-600">{error}</div>}
    {feed && (
      <div className="flex flex-col gap-4 py-3">
        {feed.sections.length === 0 && <div className="p-6 text-center text-xs text-gray-500">No section has content for this audience.</div>}
        {feed.sections.map((s) => (
          <div key={s.id}><Section s={s} /></div>
        ))}
        <div className="h-6" />
      </div>
    )}
  </div>
);

export default FeedPreview;
